"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "../drop.types";
import { DropLocation, hasDropFooter } from "../drop.types";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "../dropRankStyles";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropHeader from "../WaveDropHeader";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropRatings from "../WaveDropRatings";
import WaveDropReactions from "../WaveDropReactions";
import WaveDropReply from "../WaveDropReply";
import WinnerDropBadge from "./WinnerDropBadge";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
import { getWinnerVisibleMetadata } from "@/components/waves/winners/identity/winnerIdentity.helpers";
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
const getDropStyles = (isActiveDrop: boolean) => {
  if (isActiveDrop) {
    return {
      borderColor: "rgba(60,203,127,0.45)",
      boxShadow: "inset 0 0 0 1px rgba(60,203,127,0.2), inset 0 0 24px rgba(60,203,127,0.08)"
    };
  }
  return undefined;
};
interface DefautWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly inlineAuthorOnDesktop?: boolean | undefined;
  readonly mediaImageScale?: ImageScale | undefined;
  readonly fullWidthMedia?: boolean | undefined;
  readonly fullWidthLinkPreviews?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const DefaultWinnerDrop = t0 => {
  const $ = _c(110);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    footer,
    showReplyAndQuote,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    inlineAuthorOnDesktop: t4,
    mediaImageScale,
    fullWidthMedia: t5,
    fullWidthLinkPreviews: t6,
    winningThreshold,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const inlineAuthorOnDesktop = t4 === undefined ? false : t4;
  const fullWidthMedia = t5 === undefined ? false : t5;
  const fullWidthLinkPreviews = t6 === undefined ? false : t6;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const decisionTime = drop.winning_context?.decision_time;
  const showIdentity = identityMode !== "hidden";
  const shouldOffsetRows = showIdentity && !inlineAuthorOnDesktop;
  let t7;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t7 = getWinnerVisibleMetadata({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const visibleMetadata = t7;
  const getBackgroundColorClass = _temp;
  const bgColorClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getBackgroundColorClass(location);
  let t8;
  if ($[3] !== hasTouch || $[4] !== showInteractions) {
    t8 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[3] = hasTouch;
    $[4] = showInteractions;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleLongPress = t8;
  let t9;
  if ($[6] !== activePartIndex || $[7] !== drop || $[8] !== onReply) {
    t9 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[6] = activePartIndex;
    $[7] = drop;
    $[8] = onReply;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const handleOnReply = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsSlideUp(false);
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[11] !== activePartIndex || $[12] !== decisionTime || $[13] !== drop || $[14] !== effectiveRank || $[15] !== identityMode || $[16] !== isStorm || $[17] !== timestampLayout) {
    t11 = identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <WaveDropHeader drop={drop} showWaveInfo={false} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} badge={<WinnerDropBadge rank={effectiveRank} decisionTime={decisionTime ?? null} />} timestampLayout={timestampLayout} />;
    $[11] = activePartIndex;
    $[12] = decisionTime;
    $[13] = drop;
    $[14] = effectiveRank;
    $[15] = identityMode;
    $[16] = isStorm;
    $[17] = timestampLayout;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const identityHeader = t11;
  const t12 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  let t13;
  if ($[19] !== effectiveRank) {
    t13 = getRankStaticBorderClass(effectiveRank);
    $[19] = effectiveRank;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== effectiveRank) {
    t14 = getRankHoverClass(effectiveRank);
    $[21] = effectiveRank;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t13} tw-px-4 tw-py-3 ${bgColorClass} ${t14}`;
  let t16;
  if ($[23] !== isActiveDrop) {
    t16 = getDropStyles(isActiveDrop);
    $[23] = isActiveDrop;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t16) {
    t17 = {
      ...t16,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease"
    };
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== drop.reply_to || $[28] !== drop.wave || $[29] !== dropViewDropId || $[30] !== onReplyClick) {
    t18 = drop.reply_to && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[27] = drop.reply_to;
    $[28] = drop.wave;
    $[29] = dropViewDropId;
    $[30] = onReplyClick;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = `tw-relative tw-z-10 tw-flex tw-w-full tw-border-0 tw-bg-transparent tw-text-left ${inlineAuthorOnDesktop ? "tw-flex-col tw-gap-y-2" : "tw-gap-x-3"}`;
  let t20;
  if ($[32] !== drop || $[33] !== identityHeader || $[34] !== inlineAuthorOnDesktop || $[35] !== showIdentity) {
    t20 = inlineAuthorOnDesktop ? showIdentity && <div className="tw-flex tw-w-full tw-items-center tw-gap-x-2"><WaveDropAuthorPfp drop={drop} /><div className="tw-min-w-0 tw-flex-1">{identityHeader}</div></div> : showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[32] = drop;
    $[33] = identityHeader;
    $[34] = inlineAuthorOnDesktop;
    $[35] = showIdentity;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = showIdentity && !inlineAuthorOnDesktop && identityHeader;
  let t22;
  if ($[37] !== drop.wave || $[38] !== identityMode || $[39] !== showWaveInfo) {
    t22 = identityMode === "default" && showWaveInfo && (() => {
      const waveDetails = drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      };
      const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={_temp2} className="tw-mt-0.5 tw-text-xs tw-leading-none tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[37] = drop.wave;
    $[38] = identityMode;
    $[39] = showWaveInfo;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t21 || $[42] !== t22) {
    t23 = <div className="tw-flex tw-flex-col tw-items-start">{t21}{t22}</div>;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const t24 = showIdentity ? "tw-mt-2" : "";
  let t25;
  if ($[44] !== activePartIndex || $[45] !== drop || $[46] !== embedDepth || $[47] !== embedPath || $[48] !== fullWidthLinkPreviews || $[49] !== fullWidthMedia || $[50] !== handleLongPress || $[51] !== hasTouch || $[52] !== maxEmbedDepth || $[53] !== mediaImageScale || $[54] !== onDropContentClick || $[55] !== onQuoteClick || $[56] !== quotePath) {
    t25 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} onLongPress={handleLongPress} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} mediaImageScale={mediaImageScale} fullWidthMedia={fullWidthMedia} fullWidthLinkPreviews={fullWidthLinkPreviews} hasTouch={hasTouch} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[44] = activePartIndex;
    $[45] = drop;
    $[46] = embedDepth;
    $[47] = embedPath;
    $[48] = fullWidthLinkPreviews;
    $[49] = fullWidthMedia;
    $[50] = handleLongPress;
    $[51] = hasTouch;
    $[52] = maxEmbedDepth;
    $[53] = mediaImageScale;
    $[54] = onDropContentClick;
    $[55] = onQuoteClick;
    $[56] = quotePath;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t24 || $[59] !== t25) {
    t26 = <div className={t24}>{t25}</div>;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t23 || $[62] !== t26) {
    t27 = <div className="tw-flex tw-w-full tw-flex-col">{t23}{t26}</div>;
    $[61] = t23;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t19 || $[65] !== t20 || $[66] !== t27) {
    t28 = <div className={t19}>{t20}{t27}</div>;
    $[64] = t19;
    $[65] = t20;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== activePartIndex || $[69] !== drop || $[70] !== handleOnReply || $[71] !== isMobile || $[72] !== showInteractions || $[73] !== showReplyAndQuote) {
    t29 = !isMobile && showInteractions && showReplyAndQuote && <div className="tw-absolute tw-right-0 tw-top-1"><WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} /></div>;
    $[68] = activePartIndex;
    $[69] = drop;
    $[70] = handleOnReply;
    $[71] = isMobile;
    $[72] = showInteractions;
    $[73] = showReplyAndQuote;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  const t30 = `${shouldOffsetRows ? "tw-ml-[3.25rem]" : ""} tw-flex tw-flex-col tw-gap-2`;
  let t31;
  if ($[75] !== drop) {
    t31 = <WaveWinnerIdentity drop={drop} variant="full" cardVariant="chat" />;
    $[75] = drop;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== visibleMetadata) {
    t32 = visibleMetadata.length > 0 && <WaveDropMetadata metadata={visibleMetadata} />;
    $[77] = visibleMetadata;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== drop || $[80] !== showInteractions || $[81] !== winningThreshold) {
    t33 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{!!drop.raters_count && <WaveDropRatings drop={drop} winningThreshold={winningThreshold} />}<WaveDropReactions drop={drop} /></div>;
    $[79] = drop;
    $[80] = showInteractions;
    $[81] = winningThreshold;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t30 || $[84] !== t31 || $[85] !== t32 || $[86] !== t33) {
    t34 = <div className={t30}>{t31}{t32}{t33}</div>;
    $[83] = t30;
    $[84] = t31;
    $[85] = t32;
    $[86] = t33;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== footer || $[89] !== shouldOffsetRows) {
    t35 = hasDropFooter(footer) && <div className={`${shouldOffsetRows ? "tw-ml-[3.25rem]" : ""} tw-pb-1 tw-pt-2`}>{footer}</div>;
    $[88] = footer;
    $[89] = shouldOffsetRows;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t15 || $[92] !== t17 || $[93] !== t18 || $[94] !== t28 || $[95] !== t29 || $[96] !== t34 || $[97] !== t35) {
    t36 = <div className={t15} style={t17}>{t18}{t28}{t29}{t34}{t35}</div>;
    $[91] = t15;
    $[92] = t17;
    $[93] = t18;
    $[94] = t28;
    $[95] = t29;
    $[96] = t34;
    $[97] = t35;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== drop || $[100] !== handleOnReply || $[101] !== isSlideUp || $[102] !== longPressTriggered || $[103] !== showInteractions || $[104] !== showReplyAndQuote) {
    t37 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[99] = drop;
    $[100] = handleOnReply;
    $[101] = isSlideUp;
    $[102] = longPressTriggered;
    $[103] = showInteractions;
    $[104] = showReplyAndQuote;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  let t38;
  if ($[106] !== t12 || $[107] !== t36 || $[108] !== t37) {
    t38 = <div className={t12}>{t36}{t37}</div>;
    $[106] = t12;
    $[107] = t36;
    $[108] = t37;
    $[109] = t38;
  } else {
    t38 = $[109];
  }
  return t38;
};
export default memo(DefaultWinnerDrop);
function _temp(_loc) {
  return "tw-bg-iron-950";
}
function _temp2(e) {
  return e.stopPropagation();
}